import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { STATE_CULTURE, STATE_CURRENCY, setCurrency } from "../../common/state";
import { getInitialFromUntil } from "../../common/util";
import Busy from "./busy";
import ErrorPage from "./ErrorPage";

export const DataContext = React.createContext();

const ArticleQuery = gql`
  query ArticleQuery(
    $currency: String
    $culture: String
    $posId: Int
    $from: DateTime
    $until: DateTime
  ) {
    tags(currency: $currency, culture: $culture, posId: $posId) {
      name
      articles {
        imageFilename
        id
        plu
        name
        inStock
        priceInclVat
        price {
          amountInclVat
        }
        translation {
          culture
          name
          description
        }
        isPersonalTicket
        isProduct
        isGiftCertificate
        isPackage
        isMultiTicket
        overrideStartDate
        ticketHolders {
          categoryName
          categoryId
          maximumNumber
          defaultNumber
        }
        accessConditions {
          start
          stop
          dayOfWeeks
        }
      }
    }
    allotments(
      currency: $currency
      culture: $culture
      posId: $posId
      from: $from
      until: $until
    ) {
      id
      description
      imageFileName
      locationName
      locationId
      translation {
        culture
        name
        description
      }
      occasions {
        time
        remaining
      }
      articles {
        id
        plu
        name
        priceInclVat
        imageFilename
        isMultiTicket
        ticketHolders {
          categoryName
          categoryId
          maximumNumber
          defaultNumber
        }
        price {
          amountInclVat
        }
        translation {
          culture
          name
          description
        }
        additionalFields {
          id
          setValueOnSale
          regExValidation
          translation {
            culture
            name
            description
          }
        }
      }
    }
    articles(culture: $culture, posId: $posId) {
      imageFilename
      id
      plu
      inStock
      name
      priceInclVat
      price {
        amountInclVat
      }
      translation {
        culture
        name
        description
      }
      isPersonalTicket
      isProduct
      isMultiTicket
      isPackage
      isGiftCertificate
      ticketHolders {
        categoryName
        categoryId
        maximumNumber
        defaultNumber
      }
      additionalFields {
        id
        setValueOnSale
        regExValidation
        translation {
          culture
          name
          description
        }
      }
    }
    cultures {
      name
      description
    }
    currencies {
      name
    }
  }
`;

const DataProvider = ({ children }) => {
  const dispatch = useDispatch();
  const setEventFilterLogger = (data) => {
    setEventFilter(data);
    console.log("--> Set event date filter to:", data);
  };
  const [eventFilter, setEventFilter] = useState();
  const [translated, setTranslated] = useState(false);
  const { config, culture, currency } = useSelector((state) => ({
    config: state.config,
    culture: state.cookies[STATE_CULTURE],
    currency: state.cookies[STATE_CURRENCY],
  }));

  const dateRange = useMemo(() => {
    if (!config) return { from: null, until: null };

    if (config.initialDateRangeMonths) {
      return getInitialFromUntil(config.initialDateRangeMonths);
    } else {
      return getInitialFromUntil(12);
    }
  }, [config]);

  const { from, until } = dateRange;

  const { data, loading, error, refetch } = useQuery(ArticleQuery, {
    variables: {
      posId: config?.posId,
      currency,
      culture,
      from,
      until,
    },
    fetchPolicy: "cache-and-network",
    skip: !from || !until || !config.posId,
  });

  useEffect(() => {
    if (!loading) {
      //Set default value to empty. Will be overridden if navigating months in all-events.
      setEventFilterLogger([]);
    }
  }, [data, loading]);

  // Validate and update currency if current currency is not in available currencies
  useEffect(() => {
    if (data?.currencies && data.currencies.length > 0) {
      const availableCurrencies = data.currencies.map((c) => c.name);

      // If no currency is set, or current currency is not available, set to first available currency
      if (!currency || !availableCurrencies.includes(currency)) {
        const firstAvailableCurrency = availableCurrencies[0];
        console.log(
          `Currency ${
            currency || "not set"
          } is not available. Setting to ${firstAvailableCurrency}`
        );
        dispatch(setCurrency(firstAvailableCurrency));
      }
    }
  }, [data?.currencies, currency, dispatch]);

  console.log("DataProvider data", data);

  if (loading) return <Busy busy={loading} busyText="Laddar webshop..." />;
  if (error) return <ErrorPage showBackButton />;
  return (
    <DataContext.Provider
      value={{
        data,
        refetch,
        eventFilter,
        setEventFilter: setEventFilterLogger,
        translated,
        setTranslated,
        error,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
